import classnames from 'classnames';
import Settings from 'components/CookieOverlay/Settings';
import RoundedButton from 'components/RoundedButton';
import {
  useConsentSerialized,
  useCookieConsent,
} from 'providers/CookieConsentProvider';
import React, { useCallback, useMemo, useState } from 'react';
import { Trek } from 'utils/analytics';
import { checkAppTrackingTransparency } from '~/capacitor/appTrackingTransparency';
import { PLATFORMS } from '~/constants';

import ExcludeOnPlatforms from '../../hoc/ExcludeOnPlatforms';

const CSS = require('./CookieOverlay.module.scss');

export function CookieOverlay() {
  const { setConsent, ...consentState } = useCookieConsent();
  useConsentSerialized();

  // the localState is the unsaved/unsubmitted state
  const [localState, setLocalState] = useState<{
    showSettings: boolean;
    social: boolean;
    personalised_ads: boolean;
    analytics: boolean;
    advertisement: boolean;
    location: boolean;
    accepted: boolean;
  }>({
    showSettings: false,
    social: true,
    personalised_ads: true,
    analytics: true,
    advertisement: true,
    location: true,
    accepted: true,
  });

  const onAcceptClick = useCallback(async () => {
    const hasAppTrackingTransparency = await checkAppTrackingTransparency();

    const newConsent = {
      base: true,
      social: hasAppTrackingTransparency && localState.social,
      analytics: hasAppTrackingTransparency && localState.analytics,
      advertisement: hasAppTrackingTransparency && localState.advertisement,
      personalised_ads:
        hasAppTrackingTransparency && localState.personalised_ads,
      location: hasAppTrackingTransparency && localState.location,
    };
    setConsent(newConsent);
  }, [
    setConsent,
    localState.social,
    localState.analytics,
    localState.advertisement,
    localState.personalised_ads,
    localState.location,
  ]);

  const onChangeSettingsClick = useCallback(
    (e: any) => {
      setLocalState({ ...localState, showSettings: true });
    },
    [localState],
  );

  const onSettingChange = useCallback(
    ({
      target,
    }: {
      target: {
        checked: boolean;
        name:
          | 'social'
          | 'advertisement'
          | 'analytics'
          | 'personalised_ads'
          | 'location';
      };
    }) => {
      setLocalState({
        ...localState,
        [target.name]: target.checked,
      });
    },
    [localState],
  );
  const trekkerContext = useMemo(
    () => ({
      component: ['cookie-consent'],
      cookieConsent: {
        functional: localState.accepted,
        personalised_ads: localState.personalised_ads,
        analytics: localState.analytics,
        social: localState.social,
        advertisement: localState.advertisement,
        location: consentState.location,
      },
    }),
    [
      localState.accepted,
      localState.personalised_ads,
      localState.analytics,
      localState.social,
      localState.advertisement,
      consentState.location,
    ],
  );

  if (consentState.base) {
    return null;
  }
  return (
    <Trek context={trekkerContext}>
      <aside className={CSS.Container}>
        <div
          data-test-id="CookieOverlay"
          className={classnames(CSS.CookieOverlay, {
            [CSS.CookieOverlayWithSettings]: localState.showSettings,
          })}
        >
          <h2 className={CSS.Title}>Cookie Consent</h2>
          {!localState.showSettings && (
            <p className={CSS.Disclaimer}>
              We use cookies to make sure we give you the best user experience
              on our platform! Furthermore we use cookies for analytics &
              marketing purposes.
            </p>
          )}
          {localState.showSettings && (
            <Settings
              onSettingChange={onSettingChange}
              personalised_ads={localState.personalised_ads}
              analytics={localState.analytics}
              social={localState.social}
              advertisement={localState.advertisement}
              location={localState.location}
            />
          )}
          <div className={CSS.Footer}>
            {!localState.showSettings && (
              <button
                className={CSS.SettingsButton}
                onClick={onChangeSettingsClick}
              >
                Change settings
              </button>
            )}
            <RoundedButton
              pink
              edgeRounding="medium"
              size="S"
              className={CSS.AcceptButton}
              onClick={onAcceptClick}
              data-test-id="CookieOverlayAcceptButton"
            >
              Accept cookies
            </RoundedButton>
          </div>
        </div>
      </aside>
    </Trek>
  );
}

export default ExcludeOnPlatforms([PLATFORMS.SERVER])(CookieOverlay);
